/**
 * Courbe de rétention de la cohorte.
 *
 * Une seule série, donc pas de légende : le titre dit ce qui est tracé.
 *
 * ── Ce que la courbe dit, et ce qu'elle ne dit pas ──────────────────────────
 * Un compte est retenu à J+n s'il a agi à J+n **ou après**. La courbe est donc
 * monotone décroissante par construction — elle ne peut pas remonter, et une
 * remontée serait un bug, pas une bonne nouvelle.
 *
 * Le dénominateur change à chaque jalon : seuls les comptes qui ont eu n jours
 * pour revenir sont comptés. À J+30, la cohorte éligible est bien plus petite
 * qu'à J+1, et un taux calculé sur 30 comptes n'a pas le poids d'un taux calculé
 * sur 3 000. Le tableau donne les deux nombres, et l'axe des abscisses n'est
 * pas linéaire (1, 3, 7, 14, 30) — c'est une suite de jalons, pas une échelle
 * de temps.
 */

import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn, RetentionPoint } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<RetentionPoint>> = [
  { key: 'day', header: 'Jalon', render: (row) => (row.day === 0 ? 'J0' : `J+${row.day}`) },
  {
    key: 'eligible',
    header: 'Comptes éligibles',
    numeric: true,
    render: (row) => formatInteger(row.eligibleUsers),
  },
  { key: 'retained', header: 'Retenus', numeric: true, render: (row) => formatInteger(row.retainedUsers) },
  { key: 'rate', header: 'Taux', numeric: true, render: (row) => formatPercent(row.ratePercent) },
];

export function RetentionChart({ data }: { data: readonly RetentionPoint[] }) {
  return (
    <ChartCard
      title="Rétention de la cohorte"
      subtitle="Comptes dont la première action tombe dans la période — jalons, pas échelle de temps"
      emptyMessage="Aucun compte n'a sa première action dans cette période."
      rows={data}
      columns={columns}
      chartHeight={280}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data as RetentionPoint[]} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="day"
            // Axe CATEGORIEL et non numerique : les jalons ne sont pas
            // equidistants, et les espacer proportionnellement ecraserait J+1 et
            // J+3 contre l'origine.
            type="category"
            tickFormatter={(value: number) => (value === 0 ? 'J0' : `J+${value}`)}
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(value: number) => `${value} %`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ChartTooltip
                labelFormatter={(label) => (label === '0' ? 'Première action' : `J+${label}`)}
                valueFormatter={formatPercent}
              />
            }
          />
          <Line
            isAnimationActive={false}
            type="linear"
            dataKey="ratePercent"
            name="Rétention"
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            // Ici les points sont montres : il n'y en a que six, et ce sont des
            // mesures a des jalons precis, pas un echantillonnage continu.
            dot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
            activeDot={{ r: 6, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
